/**
 * Logger service for proper error handling and logging
 * Replaces console.error with production-ready logging
 */

export enum LogLevel {
    ERROR = 'error',
    WARN = 'warn',
    INFO = 'info',
    DEBUG = 'debug'
}

interface LogEntry {
    level: LogLevel;
    message: string;
    timestamp: string;
    context?: string;
    error?: Error;
    metadata?: Record<string, any>;
}

class LoggerService {
    private readonly isDevelopment = process.env.NODE_ENV === 'development';
    private readonly isProduction = process.env.NODE_ENV === 'production';

    /**
     * Log an error message
     */
    error(message: string, error?: Error, context?: string, metadata?: Record<string, any>): void {
        this.log(LogLevel.ERROR, message, context, error, metadata);
    }

    /**
     * Log a warning message
     */
    warn(message: string, context?: string, metadata?: Record<string, any>): void {
        this.log(LogLevel.WARN, message, context, undefined, metadata);
    }

    /**
     * Log an info message
     */
    info(message: string, context?: string, metadata?: Record<string, any>): void {
        this.log(LogLevel.INFO, message, context, undefined, metadata);
    }

    /**
     * Log a debug message (only in development)
     */
    debug(message: string, context?: string, metadata?: Record<string, any>): void {
        if (this.isDevelopment) {
            this.log(LogLevel.DEBUG, message, context, undefined, metadata);
        }
    }

    /**
     * Core logging method
     */
    private log(
        level: LogLevel,
        message: string,
        context?: string,
        error?: Error,
        metadata?: Record<string, any>
    ): void {
        const logEntry: LogEntry = {
            level,
            message,
            timestamp: new Date().toISOString(),
            context,
            error: error instanceof Error ? error : undefined,
            metadata
        };

        // In development, use console for immediate feedback
        if (this.isDevelopment) {
            this.logToConsole(logEntry, error);
        }

        // Always log critical errors to console as fallback
        if (level === LogLevel.ERROR && this.isDevelopment) {
            console.error(`[${logEntry.timestamp}] ${context || 'App'}: ${message}`, error);
        }
    }

    /**
     * Log to console for development
     */
    private logToConsole(logEntry: LogEntry, error?: Error): void {
        const prefix = `[${logEntry.timestamp}] ${logEntry.context || 'App'}:`;
        
        switch (logEntry.level) {
            case LogLevel.ERROR:
                console.error(prefix, logEntry.message, error || '');
                break;
            case LogLevel.WARN:
                console.warn(prefix, logEntry.message);
                break;
            case LogLevel.INFO:
                console.info(prefix, logEntry.message);
                break;
            case LogLevel.DEBUG:
                console.debug(prefix, logEntry.message);
                break;
        }

        if (logEntry.metadata) {
            console.table(logEntry.metadata);
        }
    }
}

// Export singleton instance
export const logger = new LoggerService();

// Convenience functions for common use cases
export const logError = (message: string, error?: Error, context?: string) => {
    logger.error(message, error, context);
};

export const logWarning = (message: string, context?: string) => {
    logger.warn(message, context);
};

export const logInfo = (message: string, context?: string) => {
    logger.info(message, context);
};

export const logDebug = (message: string, context?: string) => {
    logger.debug(message, context);
};
